import React, { useEffect } from 'react';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import CloseIcon from '@wix/da-ds/pkg/Icons/CloseBold';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import LayoutMasonryIcon from '@wix/da-ds/pkg/Icons/24x24/GalleryTorpedo';
import LayoutGridIcon from '@wix/da-ds/pkg/Icons/24x24/GalleryGrid';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ListItemRadioButton } from '@wix/da-ds/pkg/List/ListItem';
import { GalleryLayout, CropFit } from '../../types';
import NumericField from '../_partials/NumericField';
import LayoutRadioButton from '../_partials/LayoutRadioButton';
import useSettingsState, { isValidUrl } from './useSettingsState';
import { LayoutRadioValues } from './types';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import ModalLayout from '@wix/da-ds/pkg/modals/ModalLayout';
import ModalHeader from '@wix/da-ds/pkg/modals/ModalHeader';
import ModalFooter from '@wix/da-ds/pkg/modals/ModalFooter';
import s from './GallerySettingsModal.scss';
const GallerySettingsModal = ({ t, componentData, pubsub, helpers })=>{
    const { state, setGalleryLayout, setCropRules, setGridItemWidth, setGridItemHeight, setMasonryRowHeight, setSpacing, setInnerAlignment, updateLinkFor, onCancel } = useSettingsState(componentData, pubsub, helpers);
    useEffect(()=>{
        document.body.style.overflowY = 'scroll';
        return ()=>{
            document.body.style.overflowY = 'auto';
        };
    }, []);
    const renderModalContent = ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
            className: s['close-button'],
            onClick: onCancel
        }, /*#__PURE__*/ React.createElement(CloseIcon, {
            disableDefaultColors: true,
            size: IconSize.SMALLER
        })), /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset'],
            role: "radiogroup"
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.layout')), /*#__PURE__*/ React.createElement("div", {
            className: s['layout-radio-group']
        }, /*#__PURE__*/ React.createElement(LayoutRadioButton, {
            label: t('editor.gallery.settings.galleryLayout.grid'),
            value: LayoutRadioValues.GRID,
            onChange: setGalleryLayout,
            checked: state.galleryLayout === GalleryLayout.GRID,
            icon: LayoutGridIcon
        }), /*#__PURE__*/ React.createElement(LayoutRadioButton, {
            label: t('editor.gallery.settings.galleryLayout.masonry'),
            value: LayoutRadioValues.MASONRY,
            onChange: setGalleryLayout,
            checked: state.galleryLayout === GalleryLayout.MASONRY,
            icon: LayoutMasonryIcon
        }))), state.galleryLayout === GalleryLayout.GRID && /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset']
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.gridSettings')), /*#__PURE__*/ React.createElement(NumericField, {
            label: t('editor.gallery.settings.gridSettings.thumbWidth'),
            value: state.gridItemWidth,
            onChange: setGridItemWidth,
            minValue: 10,
            maxValue: 400,
            sliderStep: 10
        }), /*#__PURE__*/ React.createElement(NumericField, {
            label: t('editor.gallery.settings.gridSettings.thumbHeight'),
            value: state.gridItemHeight,
            onChange: setGridItemHeight,
            minValue: 10,
            maxValue: 400,
            sliderStep: 10
        }), /*#__PURE__*/ React.createElement(NumericField, {
            label: t('editor.gallery.settings.spacing'),
            value: state.spacing,
            onChange: setSpacing
        })), state.galleryLayout === GalleryLayout.MASONRY && /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset']
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.masonrySettings')), /*#__PURE__*/ React.createElement(NumericField, {
            label: t('editor.gallery.settings.masonrySettings.rowHeight'),
            value: state.masonryRowHeight,
            onChange: setMasonryRowHeight,
            minValue: 10,
            maxValue: 400,
            sliderStep: 10
        }), /*#__PURE__*/ React.createElement(NumericField, {
            label: t('editor.gallery.settings.spacing'),
            value: state.spacing,
            onChange: setSpacing
        })), /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset'],
            role: "radiogroup"
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.cropping')), /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            compact: true,
            name: 'cropping',
            value: 'crop',
            label: t('editor.gallery.settings.cropping.crop'),
            onChange: setCropRules,
            checked: state.cropping === CropFit.CROP
        }), /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            compact: true,
            name: 'cropping',
            value: 'fit',
            label: t('editor.gallery.settings.cropping.fit'),
            onChange: setCropRules,
            checked: state.cropping === CropFit.FIT
        })), /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset'],
            role: "radiogroup"
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.blockalign')), /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            compact: true,
            name: 'innerAlignment',
            value: 'left',
            label: t('editor.gallery.settings.blockalign.left'),
            onChange: setInnerAlignment,
            checked: state.innerAlignment === 'left'
        }), /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            compact: true,
            name: 'innerAlignment',
            value: 'center',
            label: t('editor.gallery.settings.blockalign.center'),
            onChange: setInnerAlignment,
            checked: state.innerAlignment === 'center'
        }), /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            compact: true,
            name: 'innerAlignment',
            value: 'right',
            label: t('editor.gallery.settings.blockalign.right'),
            onChange: setInnerAlignment,
            checked: state.innerAlignment === 'right'
        })), /*#__PURE__*/ React.createElement("fieldset", {
            className: s['fieldset']
        }, /*#__PURE__*/ React.createElement(InputLabelText, {
            className: s['legend']
        }, t('editor.gallery.settings.links.heading')), /*#__PURE__*/ React.createElement("p", {
            className: s['links-subheading']
        }, t('editor.gallery.settings.links.subheading')), /*#__PURE__*/ React.createElement("div", {
            className: s['links']
        }, componentData.data.map((dev, i)=>{
            var _state_itemLinks, _state_itemLinks1;
            var _state_itemLinks_dev_deviationId, _state_itemLinks_dev_deviationId1;
            return /*#__PURE__*/ React.createElement("div", {
                key: dev.deviationId
            }, /*#__PURE__*/ React.createElement(TextInput, {
                id: dev.deviationId,
                label: `Item ${i + 1}`,
                value: (_state_itemLinks_dev_deviationId = state.itemLinks[dev.deviationId]) !== null && _state_itemLinks_dev_deviationId !== void 0 ? _state_itemLinks_dev_deviationId : '',
                onChange: (e)=>updateLinkFor(dev.deviationId, e.target.value),
                placeholder: t('editor.gallery.settings.links.placeholder'),
                validationResult: ((_state_itemLinks_dev_deviationId1 = (_state_itemLinks = state.itemLinks) === null || _state_itemLinks === void 0 ? void 0 : _state_itemLinks[dev.deviationId]) !== null && _state_itemLinks_dev_deviationId1 !== void 0 ? _state_itemLinks_dev_deviationId1 : '') === '' ? undefined : isValidUrl((_state_itemLinks1 = state.itemLinks) === null || _state_itemLinks1 === void 0 ? void 0 : _state_itemLinks1[dev.deviationId]) ? 'success' : 'error'
            }));
        }))));
    return /*#__PURE__*/ React.createElement(ThemeOverride, {
        themeSurface: ThemeSurface.TERTIARY
    }, /*#__PURE__*/ React.createElement(ModalLayout, {
        className: s['container'],
        header: /*#__PURE__*/ React.createElement(ModalHeader, {
            heading: t('editor.gallery.settings.title')
        }),
        headerBorder: true,
        content: renderModalContent(),
        contentClassName: s['modal-content'],
        footerBorder: true,
        footer: /*#__PURE__*/ React.createElement(ModalFooter, {
            right: [
                /*#__PURE__*/ React.createElement(TextButton, {
                    key: "footer-cancel",
                    size: "large",
                    variant: "main",
                    onClick: onCancel
                }, t('common.cancel')),
                /*#__PURE__*/ React.createElement(SolidButton, {
                    key: "footer-done",
                    onClick: helpers.closeModal,
                    size: "large",
                    variant: "approval-green"
                }, t('common.done'))
            ]
        })
    }));
};
GallerySettingsModal.displayName = 'GallerySettingsModal';
export default GallerySettingsModal;

//# sourceMappingURL=GallerySettingsModal.js.map